import { Country } from '@ps/web-core-enums';

import type { Error } from '../types';

const IBAN_PATTERNS: { [key: string]: string } = {
  AT: '\\d{16}',
  BE: '\\d{12}',
  BG: '[A-Z]{4}\\d{6}[\\dA-Z]{8}',
  HR: '\\d{17}',
  CY: '\\d{8}[\\dA-Z]{16}',
  CZ: '\\d{20}',
  DK: '\\d{14}',
  EE: '\\d{16}',
  FI: '\\d{14}',
  FR: '\\d{10}[\\dA-Z]{11}\\d{2}',
  DE: '\\d{18}',
  GR: '\\d{7}[\\dA-Z]{16}',
  HU: '\\d{24}',
  IS: '\\d{22}',
  IE: '[\\dA-Z]{4}\\d{14}',
  IT: '[A-Z]\\d{10}[\\dA-Z]{12}',
  LV: '[A-Z]{4}[\\dA-Z]{13}',
  LI: '\\d{5}[\\dA-Z]{12}',
  LT: '\\d{16}',
  LU: '\\d{3}[\\dA-Z]{13}',
  MT: '[A-Z]{4}\\d{5}[\\dA-Z]{18}',
  NL: '[A-Z]{4}\\d{10}',
  NO: '\\d{11}',
  PL: '\\d{24}',
  PT: '\\d{21}',
  RO: '[A-Z]{4}[\\dA-Z]{16}',
  SK: '\\d{20}',
  SM: '[A-Z]\\d{10}[\\dA-Z]{12}',
  SI: '\\d{15}',
  ES: '\\d{20}',
  SE: '\\d{20}',
  UA: '\\d{25}',
  GB: '[A-Z]{4}\\d{14}'
} as const;

const countrySpecificIbanPatterns: { [key: string]: string } = {
  NL: 'NL\\d{2}[A-Z]{4}\\d{10}'
};

const allowAllEEACountries: string[] = [Country.France];

const isValid = (bankCountry: string, value: string) => {
  if (!value) {
    return true;
  }

  const country = value.substring(0, 2);

  if (
    bankCountry &&
    countrySpecificIbanPatterns[bankCountry] &&
    !new RegExp(`^${countrySpecificIbanPatterns[bankCountry]}$`).test(value)
  ) {
    return false;
  }

  if (
    (bankCountry && bankCountry !== country && !allowAllEEACountries.includes(bankCountry)) ||
    !IBAN_PATTERNS[country] ||
    !new RegExp(`^[A-Z]{2}\\d{2}${IBAN_PATTERNS[country]}$`).test(value)
  ) {
    return false;
  }

  const code = value.match(IBAN_PATTERNS[country]);

  if (!code) {
    return false;
  }

  let check = '';
  (value.substring(4) + value.substring(0, 4))
    .split('')
    .map((char) => char.charCodeAt(0) - (/\d/.test(char) ? 48 : 55))
    .join('')
    .match(new RegExp('.{1,7}', 'g'))
    ?.forEach((num) => {
      check = String(parseInt(check + num, 10) % 97);
    });

  return check === '1';
};

export const validateIBAN =
  (country: string, message?: Error['message']) =>
  (value: string): Error => ({
    valid: isValid(country, value),
    message
  });
